import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './AboutPanel.css';

interface AboutPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutPanel: React.FC<AboutPanelProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousActiveElement = document.activeElement as HTMLElement;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    if (modalRef.current) {
      modalRef.current.focus();
    }

    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
      previousActiveElement?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="about-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-title"
    >
      <div
        ref={modalRef}
        className="about-panel"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <button
          className="sticky top-2 text-gray-500 hover:text-gray-700 close-button"
          onClick={onClose}
          aria-label="Close about panel"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="about-content">
          <h2>{t('about:title')}</h2>
          <p>{t('about:intro')}</p>

          <h3>{t('about:calculation.title')}</h3>
          <p>{t('about:calculation.intro')}</p>

          <h3>{t('about:basePower.title')}</h3>
          <div className="formula-container">
            <div className="formula-box">
              <div className="formula-title">{t('about:basePower.formulaTitle')}</div>
              <div className="formula-content">{t('about:basePower.formula')}</div>
            </div>
          </div>
          <ul>
            {(t('about:basePower.points', { returnObjects: true }) as string[]).map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>

          <h3>{t('about:morePower.title')}</h3>
          <div className="formula-container">
            <div className="formula-box">
              <div className="formula-title">{t('about:morePower.formulaTitle')}</div>
              <div className="formula-content">{t('about:morePower.formula')}</div>
            </div>
          </div>
          <ul>
            {(t('about:morePower.points', { returnObjects: true }) as string[]).map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>

          <h3>{t('about:buffPower.title')}</h3>
          <div className="formula-container">
            <div className="formula-box">
              <div className="formula-title">{t('about:buffPower.formulaTitle')}</div>
              <div className="formula-content">{t('about:buffPower.formula')}</div>
            </div>
          </div>
          <ul>
            {(t('about:buffPower.points', { returnObjects: true }) as string[]).map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>

          <h3>{t('about:final.title')}</h3>
          <div className="formula-container">
            <div className="formula-box">
              <div className="formula-title">{t('about:final.formulaTitle')}</div>
              <div className="formula-content">{t('about:final.formula')}</div>
            </div>
          </div>
          <ul>
            {(t('about:final.points', { returnObjects: true }) as string[]).map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>

          <h3>{t('about:importance.title')}</h3>
          <ul>
            {(t('about:importance.points', { returnObjects: true }) as string[]).map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>

          <h3>{t('about:improve.title')}</h3>
          <ul>
            {(t('about:improve.points', { returnObjects: true }) as string[]).map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>

          <p className="final-message">{t('about:finalMessage')}</p>
        </div>
      </div>
    </div>
  );
};

export default AboutPanel;
